import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import type { Theme } from '@mui/material';
import { Box, Grid2 as Grid } from '@mui/material';
import { appBarHeight } from '@/components/AppBar/AppBar.style';
import { footerHeight } from '@/components/Footer';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useGetWatchList } from '@/pages/ValuationsV2/hooks/useGetWatchList';
import { combineSx } from '@/utils/combineSx';
import { AssetDetails } from './components/AssetDetails';
import { AssetList } from './components/AssetList/AssetList';
import { Filters } from './components/Filters';
import { useGetAssetsList } from './hooks/useGetAssetsList';
import { useSelectedAssetDetails } from './hooks/useSelectedAssetDetails';
import { cardVariants } from './theme';

const valuationsStyles = {
  root: {
    height: `calc(100vh - ${appBarHeight} - ${footerHeight})`,
    '--gap-section': '0.25rem',
    '--gap-sections': 'calc(var(--gap-section) * 3)',
  },
  valuationsMain: {
    minHeight: 0,
    zIndex: '10',
    height: '100%',
    gap: 'var(--gap-sections)',
    background: (theme: Theme) => theme.palette.primaryBackground.backgroundColor,
    borderTop: (theme: Theme) => `solid ${theme.palette.baseColors.iceBlue.c100} 1px`,
    p: {
      xs: '0 0 0 0.5rem',
      md: '0 0 0 0.625rem',
    },
  },
  assetDetailsWrapper: {
    display: 'grid',
    gap: 'var(--gap-sections)',
    overflow: 'auto',
    height: '100%',
    p: {
      xs: '0.5rem 0.5rem 0.5rem 0',
      md: '0.625rem 0.625rem 0.625rem 0',
    },
  },
  assetListContainer: [
    {
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      margin: {
        xs: '0.5rem 0 0.5rem 0',
        md: '0.625rem 0 0.625rem 0',
      },
    },
    cardVariants({ interactive: false, padding: 'none', border: true }),
  ],
};

const AssetListWrapper = () => {
  const { loadMoreAssets } = useGetAssetsList();
  useGetWatchList();
  return <AssetList onLoadMore={loadMoreAssets} />;
};

const AssetDetailsWrapper = () => {
  useSelectedAssetDetails();
  return <AssetDetails />;
};

const Valuations = () => {
  return (
    <Grid
      container
      direction='column'
      flexWrap='nowrap'
      sx={valuationsStyles.root}
      data-id='valuations-layout'
    >
      <Filters />
      <Box
        display='grid'
        component='main'
        gridTemplateColumns='repeat(12, 1fr)'
        sx={valuationsStyles.valuationsMain}
        data-id='valuations-main'
      >
        <Box
          sx={combineSx(valuationsStyles.assetListContainer)}
          data-id='valuations-asset-list-section'
          component='aside'
          gridColumn='span 3'
        >
          <AssetListWrapper />
        </Box>
        <Box
          component='section'
          sx={valuationsStyles.assetDetailsWrapper}
          data-id='valuations-asset-details-section'
          gridColumn='span 9'
        >
          <AssetDetailsWrapper />
        </Box>
      </Box>
    </Grid>
  );
};

const ValuationsV2 = () => {
  const { valuationsV2 } = useFeatureFlags();

  if (!valuationsV2) {
    return null;
  }

  return <Valuations />;
};

if (process.env.NODE_ENV === 'development') {
  loadDevMessages();
  loadErrorMessages();
}

export default ValuationsV2;
